import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Buttons.
 *
 * Spacing uses logical properties so the icon/label order mirrors under
 * `dir="rtl"`. Hover motion lives in globals.css (`[data-slot="button"]`)
 * — an isometric extrusion along the mark's 2:1 axis, not a Y-lift.
 */
const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "font-semibold select-none",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]",
    "disabled:pointer-events-none disabled:opacity-55",
    "[&_svg]:shrink-0 [&_svg]:size-[1.15em]",
  ],
  {
    variants: {
      variant: {
        primary: [
          "text-white",
          "bg-linear-to-b from-brand-500 to-brand-700",
          "hover:from-brand-400 hover:to-brand-600",
        ],
        secondary: [
          "text-[var(--content)] bg-[var(--surface)]",
          "border border-[var(--hairline-strong)]",
          "hover:border-brand-400/70 hover:bg-[var(--canvas-subtle)]",
        ],
        outline: [
          "text-brand-700 dark:text-brand-200",
          "border border-brand-500/35 bg-brand-500/8",
          "hover:bg-brand-500/14 hover:border-brand-500/60",
        ],
        ghost: [
          "text-[var(--content-muted)]",
          "hover:text-[var(--content)] hover:bg-[var(--canvas-subtle)]",
        ],
        subtle: [
          "text-[var(--content)] bg-[var(--canvas-subtle)]",
          "hover:bg-[var(--hairline)]",
        ],
        danger: ["text-white bg-danger-500 hover:bg-danger-600"],
        link: [
          "text-brand-600 dark:text-brand-300 underline-offset-4 hover:underline",
          "px-0 h-auto",
        ],
      },
      size: {
        sm: "h-9 rounded-[var(--radius-sm)] px-3.5 text-[0.8125rem]",
        md: "h-11 rounded-[var(--radius-md)] px-5 text-sm",
        lg: "h-12 rounded-[var(--radius-md)] px-5 text-[0.9375rem] sm:h-[3.25rem] sm:px-7",
        xl: "h-12 rounded-[var(--radius-md)] px-5 text-[0.9375rem] sm:h-13 sm:px-8 sm:text-base",
        icon: "size-11 rounded-[var(--radius-md)]",
        "icon-sm": "size-9 rounded-[var(--radius-sm)]",
      },
      block: { true: "w-full", false: "" },
    },
    defaultVariants: { variant: "primary", size: "md", block: false },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as the single child element (e.g. a `next/link`). */
  asChild?: boolean
}

export function Button({
  className,
  variant = "primary",
  size,
  block,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      className={cn(buttonVariants({ variant, size, block }), className)}
      {...props}
    />
  )
}

export { buttonVariants }
